import React, { useCallback, useEffect, useState } from 'react';
import {
  TextConfig,
  WidgetGroupSetting,
  WidgetGroupState
} from '@kemu-io/kemu-core/types';
import variablesManager, { getControlScopedVarName } from '@kemu-io/kemu-core/common/managers/variablesManager.js';
import classNames from 'classnames';
import { Resizable, ResizeCallbackData } from 'react-resizable';
import { DataType } from '@kemu-io/hs-types';
import { useDebouncedCallback } from 'use-debounce';
import FieldLabel from '../FieldLabel/FieldLabel';
import styles from './TextField.module.css';
import StyledInput from '@components/form-control/styledInput/styledInput';
import FormGroup from '@components/form-control/formGroup/formGroup';
import { ABORT_DRAGGING_CLASS, NO_GLOBAL_RULES_CLASS } from '@common/constants';
import useSetWidgetState from '@hooks/useSetWidgetState';
import { jsonCloneObject } from '@common/utils';
import useForceUpdate from '@hooks/useForceReload';

type Props = {
  label: string;
  variableName: string;
  recipeId: string;
  widgetId: string;
  thingRecipeId: string;
  color?: string;
  /** indicates this is the only field in the widget */
  isSingleElement: boolean;
  isHiddenField: boolean;
  onRepaintWidgetPorts: () => void;
  config: TextConfig;
  settingsIndex: number;
  onUpdateConfig: (settingsIndex: number, newConfig: TextConfig) => void;
}

const DEFAULT_W = 165;
const DEFAULT_H = 60;

const TextField = (props: Props) => {
  const {
    variableName, recipeId, thingRecipeId, isHiddenField,
    widgetId, isSingleElement, label, onRepaintWidgetPorts, config, settingsIndex, onUpdateConfig
  } = props;

  const scopedVarName = getControlScopedVarName(variableName);
  const setWidgetState = useSetWidgetState<WidgetGroupState>(recipeId, thingRecipeId, widgetId);
  const forceUpdate = useForceUpdate();

  const variableValue = variablesManager.getVariableValue({
    variableName: scopedVarName,
    readerWidgetId: widgetId,
  });

  // Local state for input value to preserve cursor position
  const currentValue = variableValue?.value as string ?? config.customValue ?? config.defaultValue ?? '';
  const [localValue, setLocalValue] = useState(currentValue);

  // Sync local state when variable value changes externally
  useEffect(() => {
    setLocalValue(currentValue);
  }, [scopedVarName, currentValue]);

  const textAreaFieldSize = {
    width: config.width || DEFAULT_W,
    height: config.height || DEFAULT_H,
  };

  const handleResize = useCallback((event: React.SyntheticEvent<Element, Event>, data: ResizeCallbackData) => {
    const size = {
      width: data.size.width,
      height: data.size.height
    };

    // Send notifications so the other fields react to the new size
    const sendStateChangeNotifications = true;

    setWidgetState((current) => {
      if (!current.settings) { return current; }
      const fieldsCopy = jsonCloneObject<WidgetGroupSetting<TextConfig>[]>(current.settings);

      // Change the width of ALL multiline fields since changing one should make the others grow/shrink
      for (const field of fieldsCopy) {
        if (field.type === 'text') {
          if (field.variableName === scopedVarName) {
            field.config.width = size.width;
            field.config.height = size.height;
          } else {
            // Other fields, only update width if in the same visibility status
            if (isHiddenField === field.customSettingField) {
              field.config.width = size.width;

              // If the field is a multiline field and the variable name is the same as the scoped variable name, update the height
              if (field.config.multiline && getControlScopedVarName(field.variableName) === scopedVarName) {
                field.config.height = size.height;
              }
            }
          }
        }
      }

      onRepaintWidgetPorts();
      return {
        ...current,
        settings: fieldsCopy
      };
    }, sendStateChangeNotifications);

  }, [setWidgetState, scopedVarName, onRepaintWidgetPorts, isHiddenField]);

  // Debounced update to variable and config
  const debouncedUpdateValue = useDebouncedCallback((value: string) => {
    variablesManager.setVariableValue({
      variableName: scopedVarName,
      value,
      setByWidgetId: widgetId,
      type: DataType.String,
    });

    onUpdateConfig(settingsIndex, {
      ...config,
      customValue: value,
    } as TextConfig);
  }, 300);

  const handleTextChange = useCallback((event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = event.target.value;
    setLocalValue(value); // Update local state immediately
    debouncedUpdateValue(value); // Update variable and config after user stops typing
  }, [debouncedUpdateValue]);

  useEffect(() => {
    const unsubscribe = variablesManager.onVariableValueChange({
      variableName: scopedVarName,
      type: DataType.Anything,
      widgetId: widgetId,
      onValueChange: forceUpdate,
    });

    return unsubscribe;
  }, [scopedVarName, widgetId, forceUpdate]);

  const hasLabel = label?.length > 0;

  return (
    <FormGroup column={true} noMarginBottom>
      <FieldLabel color={props.color} label={label} />
      {config.multiline ? (
        <Resizable
          width={textAreaFieldSize.width}
          height={textAreaFieldSize.height}
          // resizeHandles={['s']}
          minConstraints={[DEFAULT_W, DEFAULT_H]}
          onResize={handleResize}
        >
          <div
            className={ABORT_DRAGGING_CLASS}
            style={{
              width: textAreaFieldSize.width,
              height: textAreaFieldSize.height,
            }}
          >
            <textarea
              value={localValue}
              placeholder={config.placeholder}
              onChange={handleTextChange}
              className={classNames(styles.TextField, NO_GLOBAL_RULES_CLASS, {
                [styles.SingleElement]: isSingleElement && !hasLabel,
              })}
            />
          </div>
        </Resizable>
      ) : (
        <StyledInput
          value={localValue}
          placeholder={config.placeholder}
          type={config.passwordMode ? 'password' : 'text'}
          onChange={handleTextChange}
          className={classNames(styles.TextField, NO_GLOBAL_RULES_CLASS, {
            [styles.SingleElement]: isSingleElement && !label,
          })}
        />
      )}
    </FormGroup>
  );
};

export default TextField;
